import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuthInfo } from "../allauth/auth/hooks";
import ApiClient, { HttpResponse } from "../config/ApiClient";
import { useRoleLabels } from "../hooks/useRoleLabels";
import type {
    AssignmentInvitation,
    AthletePaymentAssignment,
} from "../object-actions/types/types";
import {
    assignmentHasCoachingSupport,
    clearStoredReturnParams,
    computeOnboardingSteps,
    consumeReturnTarget,
    getParticipationRole,
    getReturnSearch,
    loadStoredReturnSearch,
    OnboardingState,
    storeReturnParams,
    withReturn as withReturnUtil,
} from "../utils/onboarding";
import { useAppContext } from "./AppContext";

interface OnboardingContextType extends OnboardingState {
    loading: boolean;
    assignment: AthletePaymentAssignment | null;
    assignmentId: number | null;
    /** Pending coach/guardian invite (not yet accepted). */
    hasPendingSupportInvite: boolean;
    /** Pending guardian invite for coach onboarding (not yet accepted). */
    hasPendingGuardianInvite: boolean;
    /** Pending coach invite for guardian onboarding (not yet accepted). */
    hasPendingCoachInvite: boolean;
    /** Re-fetch assignment and recompute the checklist. */
    refresh: () => Promise<void>;
    returnSearch: string;
    withReturn: (path: string) => string;
    consumeReturn: () => string | null;
    clearReturn: () => void;
}

/** Which invite roles each onboarding role cares about while waiting on acceptance. */
type PendingInviteMode = "support" | "guardian" | "coach";

const PENDING_INVITE_ROLES: Record<PendingInviteMode, string[]> = {
    support: ["coach", "parent"],
    guardian: ["parent"],
    coach: ["coach"],
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function useOnboarding() {
    const ctx = useContext(OnboardingContext);
    if (!ctx) {
        throw new Error("useOnboarding must be used within OnboardingProvider");
    }
    return ctx;
}

interface Props {
    children: ReactNode;
}

export function OnboardingProvider({ children }: Props) {
    const { isAuthenticated, user } = useAuthInfo();
    const { organization } = useAppContext();
    const { applyRoleLabels } = useRoleLabels();
    const location = useLocation();
    const { assignment_id: assignmentIdParam } = useParams<{ assignment_id?: string }>();

    const assignmentId = useMemo(() => {
        if (!assignmentIdParam) return null;
        const parsed = parseInt(assignmentIdParam, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }, [assignmentIdParam]);

    const [assignment, setAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasPendingSupportInvite, setHasPendingSupportInvite] = useState(false);
    const [hasPendingGuardianInvite, setHasPendingGuardianInvite] = useState(false);
    const [hasPendingCoachInvite, setHasPendingCoachInvite] = useState(false);

    const [returnSearch, setReturnSearch] = useState<string>(() => {
        const fromUrl = getReturnSearch(typeof window !== "undefined" ? window.location.search : "");
        return fromUrl || loadStoredReturnSearch();
    });

    useEffect(() => {
        const fromUrl = getReturnSearch(location.search);
        if (fromUrl) {
            storeReturnParams(location.search);
            setReturnSearch(fromUrl);
        }
    }, [location.search]);

    const clearPendingInvites = useCallback(() => {
        setHasPendingSupportInvite(false);
        setHasPendingGuardianInvite(false);
        setHasPendingCoachInvite(false);
    }, []);

    const fetchPendingInvites = useCallback(async (id: number, mode: PendingInviteMode) => {
        const roles = PENDING_INVITE_ROLES[mode];
        let pending = false;
        try {
            const res = await ApiClient.get<AssignmentInvitation[]>(
                `/api/payment-assignments/${id}/invitations/`,
            );
            if (res.success && Array.isArray(res.data)) {
                pending = res.data.some(
                    (inv) => inv.action === "pending" && roles.includes(inv.role),
                );
            }
        } catch {
            pending = false;
        }
        setHasPendingSupportInvite(mode === "support" && pending);
        setHasPendingGuardianInvite(mode === "guardian" && pending);
        setHasPendingCoachInvite(mode === "coach" && pending);
    }, []);

    const fetchAssignment = useCallback(async () => {
        if (!isAuthenticated || !assignmentId) {
            setAssignment(null);
            clearPendingInvites();
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response: HttpResponse<AthletePaymentAssignment> = await ApiClient.get(
                `/api/athlete-assignments-by-id/${assignmentId}/`,
            );
            if (response.success && response.data) {
                setAssignment(response.data);
                const role = getParticipationRole(response.data.my_roles);
                if (
                    role === "athlete" &&
                    !assignmentHasCoachingSupport(response.data)
                ) {
                    await fetchPendingInvites(assignmentId, "support");
                } else if (role === "coach") {
                    await fetchPendingInvites(assignmentId, "guardian");
                } else if (role === "parent") {
                    await fetchPendingInvites(assignmentId, "coach");
                } else {
                    clearPendingInvites();
                }
            } else {
                setAssignment(null);
                clearPendingInvites();
            }
        } catch (err) {
            console.error("[ONBOARDING] Failed to load assignment", err);
            setAssignment(null);
            clearPendingInvites();
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, assignmentId, fetchPendingInvites, clearPendingInvites]);

    useEffect(() => {
        fetchAssignment();
    }, [fetchAssignment]);

    useEffect(() => {
        const handler = () => fetchAssignment();
        window.addEventListener("smsp:waiver-accepted", handler);
        return () => window.removeEventListener("smsp:waiver-accepted", handler);
    }, [fetchAssignment]);

    const state = useMemo(
        () =>
            computeOnboardingSteps({
                user,
                assignment,
                assignmentId,
                orgWaiver: organization
                    ? {
                          waiver_athlete: organization.waiver_athlete ?? organization.waiver_name,
                          waiver_adult: organization.waiver_adult,
                      }
                    : null,
                applyRoleLabels,
                hasPendingSupportInvite,
                hasPendingGuardianInvite,
                hasPendingCoachInvite,
            }),
        [user, assignment, assignmentId, organization, applyRoleLabels, hasPendingSupportInvite, hasPendingGuardianInvite, hasPendingCoachInvite],
    );

    const withReturn = useCallback((path: string) => withReturnUtil(path, returnSearch), [returnSearch]);
    const consumeReturn = useCallback(() => consumeReturnTarget(returnSearch), [returnSearch]);
    const clearReturn = useCallback(() => {
        clearStoredReturnParams();
        setReturnSearch("");
    }, []);

    const value: OnboardingContextType = {
        ...state,
        loading,
        assignment,
        assignmentId,
        hasPendingSupportInvite,
        hasPendingGuardianInvite,
        hasPendingCoachInvite,
        refresh: fetchAssignment,
        returnSearch,
        withReturn,
        consumeReturn,
        clearReturn,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
